import type {ESignerFieldData} from '@/types/esign';
import {type Ref, shallowRef} from 'vue';

export function useEsignFieldRendering(widthChange: Ref<number>) {
  const textLinesCache = new Map<string, string[]>();
  const canvasCtx = shallowRef<CanvasRenderingContext2D | null>(null);

  const tippyContent = (data: ESignerFieldData): string => {
    const label = data.title || data.toolTip || data.text;
    if (data?.primary_customer) {
      if (data.type === 'signature') {
        return 'Primary - Signature';
      }
      return `Primary - ${label}`;
    }

    if (data?.secondary_customer) {
      if (data.type === 'signature') {
        return 'Secondary - Signature';
      }
      return `Secondary - ${label}`;
    }
    return label;
  };

  const elementFillColor = (data: ESignerFieldData): string => {
    return data.type === 'signature' ? '#9970ad' : '#c0af50';
  };

  const elementClassObject = (data: ESignerFieldData) => {
    return {
      'fw-signing-element-rect': data.primary_customer ?? true,
      'fw-signing-element-rect-secondary': data.secondary_customer ?? false,
    };
  };

  const determineYTextOffset = (data: ESignerFieldData): number => {
    if (['signature', 'attachment'].includes(data.type)) {
      return data.fontSize - (widthChange.value * 7);
    }
    return (widthChange.value * 12);
  };

  const determineDisplayFontSize = (data: ESignerFieldData): number => {
    return ['signature', 'attachment'].includes(data.type)
      ? (widthChange.value * data.fontSize)
      : (widthChange.value * data.fontSize) + 10;
  };

  const getCanvasContext = (): CanvasRenderingContext2D | null => {
    if (canvasCtx.value) {
      return canvasCtx.value;
    }
    if (typeof document === 'undefined') {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvasCtx.value = canvas.getContext('2d');
    return canvasCtx.value;
  };

  const determineTextLines = (data: ESignerFieldData): string[] => {
    const text = data.text;
    if (!text) {
      return [''];
    }

    const fontSizePx = determineDisplayFontSize(data);
    const maxWidth = Math.max(0, data.w);
    const cacheKey = `${data.id ?? ''}|${fontSizePx}|${maxWidth}|${text}`;

    const cached = textLinesCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    // Fast text measurement without layout thrashing.
    const ctx = getCanvasContext();
    if (!ctx) {
      return [text];
    }

    ctx.font = `${fontSizePx}px "Lucida Console", "Andale Mono", Monaco, monospace`;

    const rows: string[] = [];
    const paragraphs = text.split('\n');

    for (const paragraph of paragraphs) {
      const words = paragraph.split(' ').filter((w) => w.length > 0);

      if (words.length === 0) {
        rows.push('');
        continue;
      }

      let current = '';

      for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;

        if (ctx.measureText(candidate).width > maxWidth && current) {
          rows.push(current);
          current = word;
        } else {
          current = candidate;
        }
      }

      if (current) {
        rows.push(current);
      }
    }

    // Basic cache with simple bound to avoid unbounded growth.
    if (textLinesCache.size > 2000) {
      textLinesCache.clear();
    }
    textLinesCache.set(cacheKey, rows);

    return rows;
  };

  return {
    tippyContent,
    elementFillColor,
    elementClassObject,
    determineYTextOffset,
    determineDisplayFontSize,
    determineTextLines,
  };
}

